import React, { useCallback, useEffect, useMemo, useState, type Dispatch, type SetStateAction } from 'react';
import { useNavigate } from 'react-router-dom';

import { Table, type TableSort } from 'src/components/TableNew';
import { CopyableCell } from 'src/components/CopyableCell';
import NoResult from 'src/components/NoResult/NoResult';
import { FilterBar, type FilterDef } from 'src/components/FilterBar';
import { useDispatch } from 'src/store/useDispatch';
import { setAppAlert } from 'src/store/slice/appAlert';
import {
  AdminWallet,
  fetchAdminWallets,
  formatWalletBalance,
  formatWalletOwnerName,
} from 'src/api/adminWallets';
import { convertDate } from 'src/components/Table/helpers';
import { useFilterQueryEffect } from 'src/hooks/useFilterQueryEffect';
import { Option, fetchCurrencyOptions } from 'src/api/reference';
import { ROUTE_USERS } from 'src/utils/routes';

import WalletDetailModal from './WalletDetailModal';
import { UserLink } from './styled';

// One bar per filter; the API ANDs them (`?owner=&address=&currency_code=`).
const WALLET_FILTERS: FilterDef[] = [
  { key: 'owner', label: 'Owner name', placeholder: 'First or "first last"' },
  { key: 'email', label: 'Owner email', placeholder: 'Starts with…' },
  { key: 'address', label: 'Address', placeholder: 'Starts with…' },
  { key: 'currency_code', label: 'Blockchain', options: [], allLabel: 'All blockchains' },
];

// Columns the API can order by, mapped from the table's field name. The other
// columns have no index, and the endpoint rejects them rather than sort a paged
// list in memory.
const SORT_FIELDS: Record<string, string> = {
  ccy: 'currency_code',
  created_on: 'created_at',
};

const HEADERS = [
  'Owner',
  'Blockchain',
  'Owner type',
  'CCY',
  'Balance',
  'Address',
  'Created on',
];

type AllWalletsTabProps = {
  onTotalChange?: Dispatch<SetStateAction<number | null>>;
};

const AllWalletsTab: React.FC<AllWalletsTabProps> = ({ onTotalChange }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [wallets, setWallets] = useState<AdminWallet[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [currencyOptions, setCurrencyOptions] = useState<Option[]>([]);
  const [selectedWallet, setSelectedWallet] = useState<AdminWallet | null>(null);
  const [sort, setSort] = useState<TableSort>(null);

  const walletFilters = useMemo(
    () =>
      WALLET_FILTERS.map((filter) =>
        filter.key === 'currency_code' ? { ...filter, options: currencyOptions } : filter,
      ),
    [currencyOptions],
  );

  useEffect(() => {
    let active = true;
    fetchCurrencyOptions().then((options) => {
      if (active) setCurrencyOptions(options);
    });
    return () => {
      active = false;
    };
  }, []);

  const load = useCallback(
    async (params: { page: number; filters: Record<string, string> }) => {
      setLoading(true);
      try {
        const res = await fetchAdminWallets({
          page: params.page,
          limit: 10,
          sort: sort ? SORT_FIELDS[sort.field] : undefined,
          order: sort?.direction ?? 'desc',
          ...params.filters,
        });
        setWallets(res.wallets);
        setTotalPages(Math.max(1, res.meta.page_count || 1));
        onTotalChange?.(res.meta.total_count || 0);
      } catch (e: any) {
        dispatch(
          setAppAlert({
            message: e?.response?.data?.error ?? 'Failed to load wallets',
            isSuccess: false,
          }),
        );
        setWallets([]);
        onTotalChange?.(0);
      } finally {
        setLoading(false);
      }
    },
    // `sort` is part of the query: a change re-runs it from page 1 via the hook.
    [dispatch, onTotalChange, sort],
  );

  const { refresh: refreshQuery } = useFilterQueryEffect({ filters, currentPage, setCurrentPage, query: load });

  const rows = useMemo(
    () =>
      wallets.map((wallet) => ({
        ...wallet,
        address_display: wallet.address,
        owner_name: formatWalletOwnerName(wallet.owner),
        owner_type_display: wallet.owner_type,
        ccy: wallet.currency_code,
        blockchain: wallet.currency_name,
        balance_display: formatWalletBalance(wallet.balance, wallet.currency_code),
        created_on: wallet.created_at,
      })),
    [wallets],
  );

  return (
    <>
      <FilterBar
        filters={walletFilters}
        values={filters}
        onChange={(key, value) => setFilters((prev) => ({ ...prev, [key]: value }))}
        onClear={() => setFilters({})}
        onRefresh={refreshQuery}
      />

      {rows.length > 0 || loading ? (
        <Table
          loading={loading}
          headers={HEADERS}
          items={rows}
          columns="1fr 0.9fr 0.8fr 0.55fr 1fr 1.1fr 0.9fr"
          fieldsToShow={[
            'owner_name',
            'blockchain',
            'owner_type_display',
            'ccy',
            'balance_display',
            'address_display',
            'created_on',
          ]}
          sortableFields={Object.keys(SORT_FIELDS)}
          sort={sort}
          onSortChange={setSort}
          totalPages={totalPages}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          disableRowDetails
          onRowClick={(item) => setSelectedWallet(item as AdminWallet)}
          renderCell={(item, field) => {
            if (field === 'address_display') {
              return <CopyableCell value={item.address_display} label="Address" />;
            }
            if (field === 'owner_name') {
              const value = String(item.owner_name || '—');
              const email = item.owner?.email;
              const ownerId = item.owner?.id;
              const canOpenUser =
                Boolean(ownerId) && String(item.owner_type || '').toLowerCase() === 'user';

              if (!canOpenUser) {
                return <span title={email ? `${value} (${email})` : value}>{value}</span>;
              }

              return (
                <UserLink
                  type="button"
                  title={email ? `${value} (${email})` : value}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`${ROUTE_USERS}/${ownerId}`);
                  }}
                >
                  {value}
                </UserLink>
              );
            }
            if (field === 'created_on') {
              if (!item.created_on) return <span>—</span>;
              return <span>{convertDate(String(item.created_on)).date}</span>;
            }
            return null;
          }}
        />
      ) : (
        <NoResult />
      )}

      {selectedWallet ? (
        <WalletDetailModal wallet={selectedWallet} onClose={() => setSelectedWallet(null)} />
      ) : null}
    </>
  );
};

export default AllWalletsTab;
